//
// Copyright (c) 2011-2020 by Curalate, Inc.
//

import { ManagedCompetitorStatus } from '@instagram-account/competitors/instagramCompetitorsConstants';
import {
  IInstagramCompetitor,
  IInstagramCompetitorWithMetrics,
} from '@instagram-account/redux/competitors/instagramCompetitorsModel';
import { InstagramMediaProductType } from '@models/IInstagramMediaItem';
import MediaType from '@models/MediaType';
import { isDefined } from '@utils/filterPredicates';
import { pluralize } from '@utils/stringUtils';

enum MEDIA_SEARCH_PREFIXES {
  USERNAME = '@',
  HASHTAG = '#',
}

export function getMediaType(
  videoId: string | undefined,
  videoTitle: string | undefined,
  mediaProductType: InstagramMediaProductType | undefined,
): MediaType {
  return isDefined(videoId) ||
    isDefined(videoTitle) ||
    (isDefined(mediaProductType) &&
      [
        InstagramMediaProductType.REELS,
        InstagramMediaProductType.IGTV,
      ].includes(mediaProductType))
    ? MediaType.Video
    : MediaType.Image;
}

export function getIsCopyrightedVideo(
  metadataMediaType: MediaType,
  videoId: string | undefined,
): boolean {
  return metadataMediaType === MediaType.Video && !isDefined(videoId);
}

function isSearchTermPrefixed(term: string): boolean {
  return (
    term.startsWith(MEDIA_SEARCH_PREFIXES.HASHTAG) ||
    term.startsWith(MEDIA_SEARCH_PREFIXES.USERNAME)
  );
}

function getTermsFromSearchString(
  prefix: MEDIA_SEARCH_PREFIXES,
  searchString?: string,
): string[] {
  if (!searchString) {
    return [];
  }
  return searchString
    .split(/[ ,]+/)
    .filter((term) => {
      // If a term starts with the provided prefix, add it as a search term.
      return term.startsWith(prefix);
    })
    .map((filteredTerm) => {
      return filteredTerm.replace(prefix, '');
    });
}

/**
 * Generates a list of hashtag strings parsed from the given searchString
 *
 * @param searchString the string to be parsed
 */
export function getHashtagsFromSearch(searchString?: string): string[] {
  return getTermsFromSearchString(MEDIA_SEARCH_PREFIXES.HASHTAG, searchString);
}

/**
 * Generates a list of username strings parsed from the given searchString
 *
 * @param searchString the string to be parsed
 */
export function getUsernamesFromSearch(searchString?: string): string[] {
  return getTermsFromSearchString(MEDIA_SEARCH_PREFIXES.USERNAME, searchString);
}

/**
 * Updates the isRemoved status of the given competitor in the list of tracked competitors
 *
 * @param competitor user to be updated
 * @param managedCompetitors list of currently managed competitors
 * @param status the management status to apply to the provided competitor
 */
export function getUpdatedManagedCompetitors(
  competitor: IInstagramCompetitor,
  managedCompetitors: IInstagramCompetitor[],
  status: ManagedCompetitorStatus,
) {
  const nonMatches = managedCompetitors.filter(
    (existingCompetitor) => existingCompetitor.username !== competitor.username,
  );
  return [...nonMatches, { ...competitor, status }].sort((a, b) =>
    a.username.localeCompare(b.username),
  );
}

function getAddedRemovedText(
  addedCompetitors: IInstagramCompetitor[],
  removedCompetitors: IInstagramCompetitor[],
): string {
  const numAdded = addedCompetitors.length;
  const numRemoved = removedCompetitors.length;

  if (numAdded > 0 && numRemoved > 0) {
    return `added ${pluralize(numAdded, 'account')} and removed ${pluralize(
      numRemoved,
      'account',
    )} from`;
  } else if (numAdded > 0) {
    return `added ${pluralize(numAdded, 'account')} to`;
  } else if (numRemoved > 0) {
    return `removed ${pluralize(numRemoved, 'account')} from`;
  } else {
    return 'updated';
  }
}

/**
 * Generates a the toast notification message based on the number of competitors added and removed
 *
 * @param addedCompetitors the list of added competitors
 * @param removedCompetitors the list of removed competitors
 */
export function generateManageToastText(
  addedCompetitors: IInstagramCompetitor[],
  removedCompetitors: IInstagramCompetitor[],
): string {
  return `You have successfully ${getAddedRemovedText(
    addedCompetitors,
    removedCompetitors,
  )} your competitors.`;
}

/**
 * Returns the list of competitors to remove
 *
 * @param trackedCompetitors the list of already tracked competitors
 * @param managedCompetitors the list of competitors from the management workflow
 */
export function getCompetitorsToRemove(
  trackedCompetitors: IInstagramCompetitorWithMetrics[],
  managedCompetitors: IInstagramCompetitor[],
): IInstagramCompetitor[] {
  // We should remove a competitor if they have been marked as ManagedCompetitorStatus.TO_REMOVE
  // AND are already tracked as a competitor. Competitors that were added via search but then marked
  // for removal before saving do not need to be included.
  return managedCompetitors.filter((competitor) => {
    const isMarkedForRemoval =
      competitor.status === ManagedCompetitorStatus.TO_REMOVE;
    const isAlreadyTracked = trackedCompetitors.some((trackedCompetitor) => {
      return trackedCompetitor.username === competitor.username;
    });
    return isMarkedForRemoval && isAlreadyTracked;
  });
}

/**
 * Returns the list of competitors to add
 *
 * @param trackedCompetitors the list of already tracked competitors
 * @param managedCompetitors the list of competitors from the management workflow
 */
export function getCompetitorsToAdd(
  trackedCompetitors: IInstagramCompetitorWithMetrics[],
  managedCompetitors: IInstagramCompetitor[],
): IInstagramCompetitor[] {
  // We should add a competitor if they have been marked as ManagedCompetitorStatus.TO_ADD
  // AND are NOT already tracked as a competitor. Already tracked Competitors that were marked
  // for removal before but then re-added saving do not need to be included.
  return managedCompetitors.filter((competitor) => {
    const isMarkedForAdd = competitor.status === ManagedCompetitorStatus.TO_ADD;
    const isAlreadyTracked = trackedCompetitors.some((trackedCompetitor) => {
      return trackedCompetitor.username === competitor.username;
    });
    return isMarkedForAdd && !isAlreadyTracked;
  });
}
